import { memo, useEffect, useState } from "react";
import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@material-ui/core";
import PropTypes from "prop-types";
import { SCROLL_CONTAINER_ID } from "./constants";
import { BRAND } from "src/theme/brandTokens";

const ITEMS = [
  { id: "seccion-portada", numero: 1, label: "Portada", sectionKey: "portada" },
  {
    id: "seccion-quienes-somos",
    numero: 2,
    label: "Quiénes somos",
    sectionKey: "quienesSomos",
  },
  {
    id: "seccion-antecedentes",
    numero: 3,
    label: "Antecedentes",
    sectionKey: "antecedentes",
  },
  {
    id: "seccion-estructura",
    numero: 4,
    label: "Estructura",
    sectionKey: "estructura",
  },
  {
    id: "seccion-servicios",
    numero: 5,
    label: "Fases",
    sectionKey: "fasesServicios",
    countKey: "itemsCount",
  },
  {
    id: "seccion-equipo",
    numero: 6,
    label: "Equipo",
    sectionKey: "equipo",
    countKey: "equipoCount",
  },
  {
    id: "seccion-info-adicional",
    numero: 7,
    label: "Info. adicional",
    sectionKey: "infoAdicional",
  },
];

/**
 * Nav lateral con scroll-spy y badges.
 * - Badge `•` naranja si la sección tiene cambios sin guardar (sectionDirty).
 * - Chip "Incompleto" si la sección tiene faltantes blandas (sectionFaltantes).
 * - Highlight de la sección visible vía IntersectionObserver.
 *
 * Recibe primitivos `itemsCount`/`equipoCount` (no `values` completo) para
 * que `React.memo` pueda hacer bail-out cuando solo cambia un campo de texto
 * del form principal.
 */
const NavLateralEdicion = memo(function NavLateralEdicion({
  itemsCount,
  equipoCount,
  sectionDirty,
  sectionFaltantes,
  onNavegar,
}) {
  const counts = { itemsCount, equipoCount };
  const [activo, setActivo] = useState("seccion-portada");

  useEffect(() => {
    // El root del scroll es la columna central del EditarPropuestaV2 (constante
    // compartida). Si no existe aún (primer render), el observer cae a viewport.
    const scrollRoot = document.getElementById(SCROLL_CONTAINER_ID);

    // El callback de IntersectionObserver solo entrega las entries cuyo ratio
    // cruzó un threshold en ese tick — NO el estado completo de todas las secciones.
    // Por eso mantenemos un mapa global con el ratio actual de cada una y elegimos
    // la de mayor ratio. Sin esto, el activo flickea al cruzar thresholds.
    const ratiosActuales = new Map();
    ITEMS.forEach((it) => ratiosActuales.set(it.id, 0));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          ratiosActuales.set(
            e.target.id,
            e.isIntersecting ? e.intersectionRatio : 0,
          );
        });
        let mejorId = null;
        let mejorRatio = 0;
        for (const [id, ratio] of ratiosActuales) {
          if (ratio > mejorRatio) {
            mejorRatio = ratio;
            mejorId = id;
          }
        }
        if (mejorId) setActivo(mejorId);
      },
      {
        root: scrollRoot,
        // Zona de detección = tope superior del scroll hasta 50% de su altura.
        // Sección activa = la que ocupa más de ese tope.
        rootMargin: "0px 0px -50% 0px",
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      },
    );
    ITEMS.forEach((it) => {
      const el = document.getElementById(it.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const hayDirtyGlobal = Object.values(sectionDirty).some(Boolean);

  return (
    <Box
      sx={{
        width: 240,
        flexShrink: 0,
        borderRight: "1px solid",
        borderColor: "divider",
        // El contenedor padre tiene altura fija (viewport - navbar); el nav
        // toma 100% de esa altura y scrolea internamente si el contenido lo requiere.
        height: "100%",
        overflowY: "auto",
        py: 2,
      }}
    >
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ px: 2, fontWeight: 600, textTransform: "uppercase" }}
      >
        Secciones
      </Typography>
      <List dense>
        {ITEMS.map((it) => {
          const esActivo = activo === it.id;
          const dirty = sectionDirty[it.sectionKey];
          const incompleta = sectionFaltantes?.[it.sectionKey]?.length > 0;
          const cantidad = it.countKey ? (counts[it.countKey] ?? 0) : null;
          return (
            <ListItemButton
              key={it.id}
              onClick={() => {
                onNavegar?.();
                const el = document.getElementById(it.id);
                if (el)
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              sx={{
                borderLeft: "3px solid",
                borderColor: esActivo ? BRAND.color.primary : "transparent",
                bgcolor: esActivo ? BRAND.color.primaryBg : "transparent",
                "&:hover": {
                  bgcolor: esActivo
                    ? BRAND.color.primaryHoverBg
                    : "action.hover",
                },
              }}
            >
              <ListItemText
                primary={
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: esActivo ? 700 : 500,
                          color: esActivo
                            ? BRAND.color.primary
                            : "text.primary",
                          flex: 1,
                        }}
                      >
                        {it.numero}. {it.label}
                        {cantidad !== null && ` (${cantidad})`}
                      </Typography>
                      {dirty && (
                        <Box
                          component="span"
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: BRAND.color.primary,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </Box>
                    {incompleta && (
                      <Chip
                        label="Incompleto"
                        size="small"
                        sx={{
                          mt: 0.5,
                          bgcolor: BRAND.color.primaryBg,
                          color: "#E65100",
                          height: 18,
                          fontSize: 10,
                        }}
                      />
                    )}
                  </Box>
                }
              />
            </ListItemButton>
          );
        })}
      </List>
      {hayDirtyGlobal && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 2,
            px: 2,
            color: BRAND.color.primary,
            fontWeight: 600,
          }}
        >
          • Cambios sin guardar
        </Typography>
      )}
    </Box>
  );
});

NavLateralEdicion.propTypes = {
  itemsCount: PropTypes.number.isRequired,
  equipoCount: PropTypes.number.isRequired,
  sectionDirty: PropTypes.object.isRequired,
  sectionFaltantes: PropTypes.object,
  onNavegar: PropTypes.func,
};

NavLateralEdicion.defaultProps = {
  sectionFaltantes: undefined,
  onNavegar: undefined,
};

export default NavLateralEdicion;
